import type { LeadDetail } from '@performance-crm/shared';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { useSetConsent } from '@/lib/queries';

/**
 * Diálogo propio para marcar «no contactar» (motivo obligatorio) o registrar un consentimiento (motivo obligatorio
 * solo cuando levanta un «no contactar»). La API se llama únicamente al confirmar: cancelar o cerrar no guarda nada.
 */
export function ConsentDialog({
  lead,
  mode,
  open,
  onOpenChange,
}: {
  lead: LeadDetail;
  mode: 'optOut' | 'grant';
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const consent = useSetConsent(lead.id);
  const toast = useToast();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const lifting = mode === 'grant' && !lead.contact.consent.contactable;
  const required = mode === 'optOut' || lifting;
  const prefix = mode === 'optOut' ? 'optout' : 'consent';
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const text = reason.trim();
    if (required && !text) {
      toast.error('Falta el motivo', 'Escribe quién lo pidió y cómo.');
      return;
    }
    try {
      await consent.mutateAsync(
        mode === 'optOut'
          ? { granted: false, source: null, reason: text }
          : { granted: true, source: 'MANUAL', reason: text || null },
      );
      toast.success(
        mode === 'optOut' ? 'Se marcó como «no contactar»' : 'Consentimiento registrado',
      );
      onOpenChange(false);
    } catch (err) {
      toast.error('No se pudo actualizar', (err as Error).message);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {mode === 'optOut'
                ? 'Marcar «no contactar»'
                : lifting
                  ? 'Registrar nuevo consentimiento'
                  : 'Registrar consentimiento'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'optOut'
                ? `${lead.contact.firstName} dejará de recibir WhatsApp y mensajes del bot hasta que registres un nuevo consentimiento. Si cancelas, no se guarda nada.`
                : lifting
                  ? `${lead.contact.firstName} volverá a recibir WhatsApp y mensajes del bot. Indica quién volvió a autorizar y cómo.`
                  : 'Anota cómo autorizó que le contactemos. Si cancelas, no se guarda nada.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor={`${prefix}-reason`}>{required ? 'Motivo' : 'Motivo (opcional)'}</Label>
            <Textarea
              id={`${prefix}-reason`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder={
                mode === 'optOut'
                  ? 'Ej. pidió por WhatsApp que no le escriban más'
                  : 'Ej. volvió a autorizar por teléfono'
              }
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid={`${prefix}-cancel`}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant={mode === 'optOut' ? 'destructive' : 'default'}
              disabled={consent.isPending}
              data-testid={`${prefix}-confirm`}
            >
              {mode === 'optOut' ? (
                <ShieldOff className="h-4 w-4" />
              ) : (
                <ShieldCheck className="h-4 w-4" />
              )}{' '}
              Confirmar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Compatibilidad: el diálogo de «no contactar». */
export function OptOutDialog(props: {
  lead: LeadDetail;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  return <ConsentDialog mode="optOut" {...props} />;
}
